import React, { useState, useEffect, useContext, useRef } from 'react';
import {isTitleColumn, resolveAppearance} from '../utils/uiUtils';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Form } from 'react-bootstrap';
import { Form as FormType, Screen, Button as ButtonType } from '../types';
import { apiService } from '../services/api';
import { SelectModal, ScreenDataTable, FormFieldRenderer, Breadcrumb, NestedTableField } from '../components';
import { ToastNotification } from '../components';
import { ConfirmModal } from '../components/ConfirmModal';
import { WorkflowModal } from '../components/WorkflowModal';
import { useFilters } from '../contexts';
import { useFormFields, useSelectModal, useOneToManyScreens, useToast, useFormNavigation, useIncomingNotice } from '../hooks';
import { AppContext } from '../contexts/AppContext';
import { handleMutationError, handleMutationSuccess } from '../utils/formMutation';
import { processFormData, getChangedData } from '../utils/formDataProcessing';
import { buildInitialFormData } from '../utils/formInitialization';
import { applyRowContextFilters, recordLinkCodes, subTableFilters } from '../utils/contextFilters';
import { applyWorkflowRedirect, readWorkflowMessage, readWorkflowRedirect } from '../utils/workflowResult';
import { downloadFile, FileData } from '../utils/fileUtils';
import { breadcrumbStack } from '../utils/breadcrumbStack';
import { appScreenPath } from '../utils/screenPath';
import { resolveAfterSave } from '../utils/afterSaveNavigation';

interface UpdateFormPageProps {
    form: FormType;
    rowData: any;
    appId: string;
    allScreens: Screen[];
    /** Rendered inside a modal: no breadcrumb, no page chrome, no navigation after saving. */
    embedded?: boolean;
    onSuccess?: (data?: any) => void;
}

export const UpdateFormPage: React.FC<UpdateFormPageProps> = ({ form, rowData, appId, allScreens, embedded = false, onSuccess }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();
    const { selectedApplication } = useContext(AppContext);

    // Breadcrumb stack sync (clear on fresh PUSH, heal on POP/refresh) is
    // handled centrally in App.tsx (BreadcrumbNavigationSync).

    const [initialFormData, setInitialFormData] = useState<Record<string, any>>({});
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [showWorkflowModal, setShowWorkflowModal] = useState(false);
    const [workflowLoading, setWorkflowLoading] = useState(false);
    const [workflowSuccess, setWorkflowSuccess] = useState<boolean | undefined>(undefined);
    const [workflowMessage, setWorkflowMessage] = useState<string | undefined>(undefined);
    const [workflowTitle, setWorkflowTitle] = useState('');
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmConfig, setConfirmConfig] = useState<{title: string, body: string, confirmButtonText: string, onConfirm: () => void} | null>(null);
    const { toasts, showToast, showSuccess, showError, hideToast } = useToast();
    const { getFilter, setFilter, removeFilter } = useFilters();

    useIncomingNotice(showToast);

    const { formData, displayValues, rawValues, setFormData, setDisplayValues, setRawValues, handleInputChange } = useFormFields();
    const { selectModalConfig, openSelectModal, closeSelectModal } = useSelectModal();
    const { oneToManyScreens } = useOneToManyScreens(form, allScreens);
    // Explicit set of field codes with unsaved changes. The Save button shows
    // iff it's non-empty. This replaces fragile baseline-diffing: each operation
    // only touches its OWN field's membership, so e.g. a many-to-one direct save
    // removes just that field while pending text/checkbox edits stay dirty.
    const [dirtyFields, setDirtyFields] = useState<Set<string>>(new Set());
    const hasChanges = dirtyFields.size > 0;
    // Many-to-one fields whose raw value is a row picked from the field's screen
    // since the record was last read; every other raw value came with the record.
    const [pickedFields, setPickedFields] = useState<Set<string>>(new Set());

    const isFieldEqual = (a: any, b: any): boolean => {
        const aEmpty = a === '' || a === null || a === undefined;
        const bEmpty = b === '' || b === null || b === undefined;
        if (aEmpty && bEmpty) return true;
        if (Array.isArray(a) && Array.isArray(b)) return JSON.stringify(a) === JSON.stringify(b);
        if ((typeof a === 'string' || typeof a === 'number') && (typeof b === 'string' || typeof b === 'number')) return String(a) === String(b);
        return a === b;
    };

    const markFieldDirty = (code: string, value: any) => {
        setDirtyFields(prev => {
            const next = new Set(prev);
            const equal = isFieldEqual(value, initialFormData[code]);
            if (equal) next.delete(code);
            else next.add(code);
            return next;
        });
    };

    const { blocker, navigateBack, navigateTo, handleConfirmNavigation, handleCancelNavigation, isNavigatingRef } = useFormNavigation({
        hasUnsavedChanges: !embedded && hasChanges
    });

    const screen = allScreens.find(s => s.id === form.screenId);
    const oneToManyFields = form.fields.filter(f => f.type === 'one-to-many');
    const recordScreen = screen;

    // Find column with ui="title" to use its value in breadcrumb
    const titleColumn = screen?.columns?.find(isTitleColumn);
    const rawTitleValue = titleColumn ? rowData[titleColumn.code] : null;
    const titleValue = rawTitleValue && typeof rawTitleValue === 'object'
        ? rawTitleValue[titleColumn?.targetColumn?.columnName ?? 'name'] ?? ''
        : rawTitleValue;
    const breadcrumbTitle = titleValue || form.displayName;

    // Deep-links (e.g. from workflow e-mails) can opt into parent-screen
    // crumbs via ?fromScreen=…, ordered top-down. Two formats:
    //   - "idA,idB"                            → plain clickable screen crumbs
    //   - JSON: [{"screen":"idA"},{"screen":"idB","filters":{"project_id":"5"},"label":"Projekt X"}]
    //     filters = context filters applied when the crumb is clicked (so a
    //     nested screen opens scoped to the row it was drilled through);
    //     label   = row title shown as the crumb between screens.
    // Only seeds a cold open — in-app navigation fills the stack itself and
    // takes precedence.
    useEffect(() => {
        const raw = new URLSearchParams(location.search).get('fromScreen');
        if (!raw || breadcrumbStack.get().length > 0) return;

        type TrailItem = { screen: string; filters?: Record<string, string>; label?: string };
        let items: TrailItem[];
        if (raw.trim().startsWith('[')) {
            try {
                items = JSON.parse(raw);
            } catch {
                return;
            }
        } else {
            items = raw.split(',').map(id => ({ screen: id.trim() }));
        }

        const entries = items.flatMap(item => {
            const fromScreen = allScreens.find(s => String(s.id) === String(item.screen));
            if (!fromScreen) return [];
            return [
                {
                    label: fromScreen.displayName,
                    path: appScreenPath({ id: appId, screens: allScreens }, fromScreen.id),
                    ...(item.filters ? { filters: item.filters } : {})
                },
                ...(item.label ? [{ label: item.label }] : [])
            ];
        });
        if (entries.length === 0) return;
        breadcrumbStack.push(entries, location.pathname);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const previousRowIdRef = useRef<string | number | null>(null);
    const focusedRecordRef = useRef<string | number | null>(null);

    // Header field first; with nothing typable there the polling waits for the
    // one-to-many tables and takes their first editable cell instead.
    const HEADER_SELECTOR =
        'form input:not([disabled]):not([readonly]):not([type="hidden"]), ' +
        'form textarea:not([disabled]):not([readonly])';
    const GRID_SELECTOR = 'tbody input:not([disabled]):not([readonly])';

    useEffect(() => {
        if (!rowData?.id || focusedRecordRef.current === rowData.id) return;

        const deadline = Date.now() + 6000;
        const timer = window.setInterval(() => {
            const active = document.activeElement;
            if (active && active !== document.body && active.tagName !== 'DIV') {
                window.clearInterval(timer);
                return;
            }

            const target = document.querySelector<HTMLElement>(HEADER_SELECTOR)
                ?? document.querySelector<HTMLElement>(GRID_SELECTOR);

            if (target) {
                target.focus();
                focusedRecordRef.current = rowData.id;
                window.clearInterval(timer);
            } else if (Date.now() > deadline) {
                window.clearInterval(timer);
            }
        }, 200);

        return () => window.clearInterval(timer);
    }, [rowData?.id]);


    /** Values handed over by a workflow redirect; only fields this form knows. */
    const consumePrefill = (): Record<string, any> => {
        const prefill = (location.state as any)?.prefill;
        if (!prefill || typeof prefill !== 'object') return {};

        return Object.fromEntries(
            Object.entries(prefill).filter(([code]) => form.fields.some(field => field.code === code)),
        );
    };


    // The row handed over by the screen carries only the screen's columns, so a
    // form field the screen does not show (dynamic forms read the record on
    // their own) arrives later with the detail fetch. id/updated_at are equal in
    // both, so they cannot drive the re-init — track the field values instead.
    const rowSignature = form.fields
        .map(field => `${field.code}=${JSON.stringify(rowData?.[field.code] ?? null)}`)
        .join('|');

    useEffect(() => {
        // Transient / minimal rowData (e.g. the detail query is briefly without
        // data during a background refetch triggered by a child save or an M2O
        // pick). Do NOT wipe — keep the current form state and the dirty set,
        // otherwise unsaved checkbox/text edits and the Save button vanish.
        if (!rowData.id || Object.keys(rowData).length <= 1) {
            return;
        }

        // String-safe so a number id from the server vs a string id from the
        // URL params don't read as a "new record" and trigger a reset.
        const isNewRecord = String(previousRowIdRef.current) !== String(rowData.id);
        previousRowIdRef.current = rowData.id;

        const { initialData, initialDisplayValues, initialRawValues } = buildInitialFormData(
            form.fields,
            rowData,
            getFilter,
            screen?.columns
        );

        if (isNewRecord) {
            // Fresh navigation to a different record — full reset.
            // A workflow redirect can hand over values to overwrite; they are
            // NOT saved yet, so they count as unsaved changes and the Save
            // button must appear. The baseline stays the record's own values.
            const prefill = consumePrefill();

            // Publish this row's context, exactly as clicking it in the list
            // does. Without it a stale value from a previously opened record
            // survives and the embedded child tables show the wrong record's
            // rows - the form prefers the stored context over the open record.
            if (screen) {
                applyRowContextFilters(screen, rowData, setFilter, removeFilter);
            }

            setFormData({ ...initialData, ...prefill });
            setInitialFormData(initialData);
            setDisplayValues(initialDisplayValues);
            setRawValues(initialRawValues);
            setPickedFields(new Set());
            setDirtyFields(new Set(Object.keys(prefill)));
            return;
        }

        // Same record, server data refreshed (background refetch). Take fresh
        // server values for every field EXCEPT the ones the user is actively
        // editing (still in dirtyFields) — those keep the pending value. The
        // dirty set itself is left untouched.
        const merged: Record<string, any> = { ...initialData };
        Array.from(dirtyFields).forEach(code => {
            if (code in formData) merged[code] = formData[code];
        });
        setFormData(merged);
        setInitialFormData(initialData);
        setDisplayValues(initialDisplayValues);
        setRawValues(initialRawValues);
        setPickedFields(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rowData.id, rowData.updated_at, rowSignature]);

    const handleInputChangeWithTracking = (code: string, value: any) => {
        handleInputChange(code, value);
        markFieldDirty(code, value);
    };

    const updateMutation = useMutation({
        mutationFn: (changedData: Record<string, any>) => {
            return form.kind === 'dynamic'
                ? apiService.updateDynamicFormData(form.id, rowData.id, changedData)
                : apiService.updateFormData(form.id, rowData.id, changedData);
        },
        onSuccess: (response) => {
            if (!response.success) {
                handleMutationError({ response, setFieldErrors, showError });
                return;
            }

            handleMutationSuccess({ setFieldErrors });
            // The table's UPDATE workflow may answer with its own notice next to
            // the record; without one the generic confirmation is shown.
            const notice = readWorkflowMessage(response.data?.data || response.data);
            if (notice) {
                showToast(notice.text, notice.type);
            } else {
                showSuccess('Úspešne uložené');
            }
            // Note: dirty-state is cleared by the specific caller (full submit
            // clears all; a many-to-one direct save drops only its own field).
        },
        onError: (error: any) => {
            showError(error.message || 'Chyba pri ukladaní');
        }
    });

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const changedData = getChangedData(formData, initialFormData);
        const processedData = processFormData(changedData, form.fields);

        updateMutation.mutate(processedData, {
            onSuccess: async (response) => {
                if (response.success) {
                    // Refetch screen data and wait for it to complete before navigating back.
                    // Dynamic forms read their record from the form endpoint (own
                    // query key) — refresh that too, or a back/remount would show
                    // the pre-save values from the 5-minute query cache.
                    await Promise.all([
                        queryClient.refetchQueries({ queryKey: ["screenData"] }),
                        queryClient.refetchQueries({ queryKey: ["dynamicFormData"] }),
                    ]);
                    if (embedded) {
                        // The modal owner decides what happens next; nothing to navigate.
                        setDirtyFields(new Set());
                        onSuccess?.((response.data as any)?.data ?? response.data);
                        return;
                    }
                    const afterUpdate = resolveAfterSave(form.afterUpdate, {
                        app: selectedApplication,
                        appId,
                        recordId: rowData?.id,
                        currentFormId: form.id,
                    });

                    if (afterUpdate.kind === 'stay') {
                        // The update response carries the full record AFTER any
                        // workflow ran — merge only the returned values into the
                        // form so workflow-applied changes show up. Don't rebuild
                        // the whole form state from scratch.
                        const updatedRecord = (response.data as any)?.data ?? response.data;
                        if (updatedRecord && typeof updatedRecord === 'object') {
                            setFormData(prev => {
                                const merged = { ...prev };
                                for (const key of Object.keys(updatedRecord)) {
                                    if (key in merged) {
                                        merged[key] = updatedRecord[key];
                                    }
                                }
                                return merged;
                            });
                            setInitialFormData(prev => {
                                const merged = { ...prev };
                                for (const key of Object.keys(updatedRecord)) {
                                    if (key in merged) {
                                        merged[key] = updatedRecord[key];
                                    }
                                }
                                return merged;
                            });
                        } else {
                            setInitialFormData(formData);
                        }
                        setDirtyFields(new Set());
                        return;
                    }
                    // Save succeeded and we're leaving — drop the dirty state so
                    // the unsaved-changes blocker doesn't fire on the way out,
                    // and flag the programmatic navigation as our own.
                    isNavigatingRef.current = true;
                    setDirtyFields(new Set());
                    if (afterUpdate.kind === 'navigate') {
                        navigateTo(afterUpdate.path);
                    } else {
                        navigateBack();
                    }
                }
            }
        });
    };

    const handleModalSelect = async (row: any, displayValue: string) => {
        if (selectModalConfig) {
            const fieldCode = selectModalConfig.fieldCode;

            // Functional update — merge into the LATEST form state so a stale
            // closure (modal opened before a pending checkbox/text edit) can't
            // overwrite those other unsaved values back to their old version.
            setFormData(prev => ({ ...prev, [fieldCode]: row.id }));

            setDisplayValues(prev => ({
                ...prev,
                [fieldCode]: displayValue
            }));

            setRawValues(prev => ({
                ...prev,
                [fieldCode]: row
            }));
            setPickedFields(prev => new Set(prev).add(fieldCode));

            if (fieldErrors[fieldCode]) {
                setFieldErrors(prev => ({ ...prev, [fieldCode]: '' }));
            }

            const changed = row.id !== initialFormData[fieldCode];

            if (changed) {
                const changedData = {
                    [fieldCode]: row.id
                };

                updateMutation.mutate(changedData, {
                    onSuccess: () => {
                        // Only the M2O field was persisted. Update its baseline
                        // (functional update — no stale closure across multiple
                        // direct saves) and drop ONLY this field from the dirty
                        // set, so other pending edits keep the Save button shown.
                        setInitialFormData(prev => ({ ...prev, [fieldCode]: row.id }));
                        setDirtyFields(prev => {
                            const next = new Set(prev);
                            next.delete(fieldCode);
                            return next;
                        });
                    }
                });
            }
        }
    };

    const handleFieldClear = (field: any) => {
        const newFormData = { ...formData, [field.code]: null };
        setFormData(newFormData);
        setDisplayValues(prev => ({ ...prev, [field.code]: '' }));
        setRawValues(prev => {
            const next = { ...prev };
            delete next[field.code];
            return next;
        });
        markFieldDirty(field.code, null);
    };

    // The entry carries this form's own path so the crumb is clickable and
    // heal() can recognise a leftover after browser-back.
    const pushCurrentToBreadcrumb = (destPath: string) => {
        breadcrumbStack.push([{ label: breadcrumbTitle, path: location.pathname }], destPath);
        breadcrumbStack.markDrilldown();
    };

    const handleNavigateToScreen = (screenId: string, titleValue?: string) => {
        const dest = `/app/${appId}/screen/${screenId}`;
        pushCurrentToBreadcrumb(dest);
        navigate(dest);
    };

    const handleOpenCreateForm = (nestedForm: any) => {
        const hasOneToMany = nestedForm.fields?.some((field: any) => field.type === 'one-to-many');
        const dest = hasOneToMany
            ? `/app/${appId}/create-form/${nestedForm.id}`
            : `/app/${appId}/create-simple-form/${nestedForm.id}`;
        pushCurrentToBreadcrumb(dest);
        navigate(dest);
    };

    const handleOpenUpdateForm = (nestedForm: any, nestedRowData: any) => {
        const hasOneToMany = nestedForm.fields?.some((field: any) => field.type === 'one-to-many');
        const dest = hasOneToMany
            ? `/app/${appId}/update-form/${nestedForm.id}/${nestedRowData.id}`
            : `/app/${appId}/update-simple-form/${nestedForm.id}/${nestedRowData.id}`;
        pushCurrentToBreadcrumb(dest);
        navigate(dest, { state: { rowData: nestedRowData } });
    };

    const executeWorkflowAction = async (button: ButtonType) => {
        setWorkflowTitle(button.displayName);
        setWorkflowLoading(true);
        setWorkflowSuccess(undefined);
        setWorkflowMessage(undefined);
        setShowWorkflowModal(true);

        try {
            const response = await apiService.executeWorkflow(button.url, {data: rowData});

            if (response.success) {
                const responseData = response.data?.data || response.data;
                let fileData: FileData | null = null;

                if (responseData?.file && typeof responseData.file === 'object' && responseData.file.mimeType) {
                    fileData = responseData.file as FileData;
                } else if (responseData?.fileData && typeof responseData.fileData === 'object' && responseData.fileData.mimeType) {
                    fileData = responseData.fileData as FileData;
                } else if (responseData?.mimeType && (responseData?.data || responseData?.url)) {
                    fileData = responseData as FileData;
                }

                if (fileData) {
                    await downloadFile(fileData);
                    setWorkflowSuccess(true);
                    setWorkflowMessage('Súbor bol úspešne stiahnutý');
                } else {
                    // A workflow can set its own notice with a type. Typed
                    // notices go out as toasts so an 'error' one reads as an
                    // error (and stays until dismissed); without one the modal
                    // keeps showing the plain confirmation.
                    const notice = readWorkflowMessage(responseData);
                    if (notice && !responseData?.redirectToScreen && !responseData?.redirectToForm) {
                        setShowWorkflowModal(false);
                        showToast(notice.text, notice.type);
                    } else if (!notice) {
                        setWorkflowSuccess(true);
                        setWorkflowMessage(undefined);
                    }
                }

                queryClient.invalidateQueries({queryKey: ["screenData"]});

                // A workflow can also say where to go next, what the app should
                // remember and what the target form starts with. Downloading a
                // file and leaving the page exclude each other, so a redirect is
                // only followed when nothing was downloaded.
                const redirect = fileData ? null : readWorkflowRedirect(responseData);
                if (redirect) {
                    const moved = applyWorkflowRedirect(redirect, {
                        app: selectedApplication,
                        appId,
                        notice: readWorkflowMessage(responseData),
                        fallbackRecordId: rowData?.id,
                        setFilter,
                        removeFilter,
                        navigate,
                    });
                    if (moved) {
                        isNavigatingRef.current = true;
                        setShowWorkflowModal(false);
                    }
                }
            } else {
                let errorMessage = response.message || 'Nastala chyba pri vykonávaní workflow';
                if (response.errors) {
                    if (Array.isArray(response.errors) && response.errors.length > 0) {
                        errorMessage = response.errors.join('\n');
                    } else if (typeof response.errors === 'object') {
                        const allErrors: string[] = [];
                        Object.values(response.errors).forEach((fieldErrors) => {
                            if (Array.isArray(fieldErrors)) {
                                allErrors.push(...fieldErrors);
                            }
                        });
                        if (allErrors.length > 0) {
                            errorMessage = allErrors.join('\n');
                        }
                    }
                }
                setWorkflowSuccess(false);
                setWorkflowMessage(errorMessage);
            }
        } catch (error) {
            setWorkflowSuccess(false);
            setWorkflowMessage('Nastala chyba pri vykonávaní workflow');
        } finally {
            setWorkflowLoading(false);
        }
    };

    const handleWorkflowButtonClick = (button: ButtonType, e: React.MouseEvent) => {
        e.preventDefault();

        if (button.confirm) {
            setConfirmConfig({
                ...button.confirm,
                onConfirm: () => executeWorkflowAction(button),
            });
            setShowConfirmModal(true);
        } else {
            executeWorkflowAction(button);
        }
    };

    const handleWorkflowModalClose = () => {
        setShowWorkflowModal(false);
        setTimeout(() => {
            setWorkflowLoading(false);
            setWorkflowSuccess(undefined);
            setWorkflowMessage(undefined);
        }, 300);
    };

    const renderField = (field: any) => {
        if (field.context) {
            return null;
        }

        if (field.type === 'one-to-many') {
            return null;
        }

        return (
            <FormFieldRenderer
                key={field.code}
                field={field}
                value={formData[field.code] ?? ''}
                error={fieldErrors[field.code]}
                onChange={(value) => handleInputChangeWithTracking(field.code, value)}
                onErrorClear={() => setFieldErrors(prev => ({ ...prev, [field.code]: '' }))}
                disabled={field.editable === false}
                displayValue={displayValues[field.code]}
                onOpenSelectModal={(field) => openSelectModal(field, screen?.columns)}
                allScreens={allScreens}
                rawValue={rawValues[field.code]}
                rawValueFromRecord={!pickedFields.has(field.code)}
                relatedRecord={rowData?._related?.[field.code]?.[field.screenId]}
                onClear={() => handleFieldClear(field)}
                breadcrumbEntry={{ label: breadcrumbTitle, path: location.pathname }}
                autoFocus={field.code === autoFocusFieldCode}
                formValues={formData}
                formScreen={screen}
            />
        );
    };

    const sortedFields = [...form.fields].sort((a, b) => a.sortOrder - b.sortOrder);
    const regularFields = sortedFields.filter(f => {
        return f.type !== 'one-to-many';

    });

    // Same as creating: the cursor starts in the first field that takes input.
    // No scanner here though - editing is never interrupted by the camera.
    const autoFocusFieldCode = regularFields.find(f => !f.context && f.editable !== false)?.code;

    return (
        <>
            {!embedded && (
                <Breadcrumb currentLabel={form.displayName} appId={appId} subtitleFallback={titleValue ? String(titleValue) : null} />
            )}
            <div className={embedded ? undefined : 'page-body'}>
                <div className={embedded ? undefined : 'container-fluid'}>
            <ToastNotification
                toasts={toasts}
                onClose={hideToast}
            />
            <div className={embedded ? undefined : 'card'}>
                <div className={embedded ? undefined : 'card-body'}>
                    {!embedded && <h3 className="mb-3">{form.displayName}</h3>}

                    <Form onSubmit={handleSubmit}>
                        {regularFields.map(field => renderField(field))}

                        <div className="d-flex justify-content-between align-items-center gap-2 mt-3">
                            {form.buttons && form.buttons.length > 0 && (
                                <div className="d-flex gap-2">
                                    {form.buttons
                                        .sort((a, b) => a.sortOrder - b.sortOrder)
                                        .map((button) => (
                                            <Button
                                                key={button.id}
                                                type="button"
                                                variant={button.appearance ? undefined : 'outline-secondary'}
                                                className={`btn-pill ${resolveAppearance(button.appearance, 'button').className}`}
                                                style={resolveAppearance(button.appearance, 'button').style}
                                                onClick={(e) => handleWorkflowButtonClick(button, e)}
                                            >
                                                {button.displayName}
                                            </Button>
                                        ))
                                    }
                                </div>
                            )}

                            {hasChanges && (
                                <div className="d-flex gap-2 ms-auto">
                                    <Button variant="primary" className="btn-pill" type="submit" data-e2e={`submit:${form.id}`} disabled={updateMutation.isPending}>
                                        {updateMutation.isPending ? 'Ukladám...' : 'Uložiť'}
                                    </Button>
                                </div>
                            )}
                        </div>
                    </Form>
                </div>
            </div>

            {oneToManyFields.map(field => {
                const screen = oneToManyScreens[field.code];
                if (!screen) return null;

                const screenFilters: Record<string, any> = rowData.id
                    ? subTableFilters(screen, rowData.id, getFilter, recordLinkCodes(recordScreen, field))
                    : {};

                // displayAsTable: read-only single flat table of the leaf
                // records (drills through single-o2m wrapper screens).
                if (field.displayAsTable) {
                    return (
                        <div key={field.code}>
                            <NestedTableField
                                screen={screen}
                                filters={screenFilters}
                                title={field.displayName}
                                parentFormId={form.id}
                                parentRecordId={rowData.id}
                            />
                        </div>
                    );
                }

                return (
                    <div key={field.code} className="mt-3">
                        <ScreenDataTable
                            screen={screen}
                            filters={screenFilters}
                            onNavigateToScreen={handleNavigateToScreen}
                            onOpenUpdateForm={handleOpenUpdateForm}
                            onOpenCreateForm={handleOpenCreateForm}
                            embedded={true}
                            editable={field.editable}
                            embeddedTitle={field.displayName}
                            parentFormId={form.id}
                            parentRecordId={rowData.id}
                        />
                    </div>
                );
            })}

            {selectModalConfig && (
                <SelectModal
                    show={selectModalConfig.show}
                    onHide={closeSelectModal}
                    screenId={selectModalConfig.screenId}
                    onSelect={handleModalSelect}
                    screenData={selectModalConfig.screenData}
                    formValues={formData}
                    formScreen={screen}
                />
            )}

            {!embedded && blocker.state === "blocked" && (
                <ConfirmModal
                    show={true}
                    title="Neuložené zmeny"
                    message="Máte neuložené zmeny. Naozaj chcete zahodiť zmeny a opustiť túto stránku?"
                    confirmText="Zahodiť zmeny"
                    cancelText="Zrušiť"
                    autoHideOnConfirm={false}
                    onConfirm={handleConfirmNavigation}
                    onHide={handleCancelNavigation}
                />
            )}

                <WorkflowModal
                    show={showWorkflowModal}
                    onHide={handleWorkflowModalClose}
                    title={workflowTitle}
                    isLoading={workflowLoading}
                    success={workflowSuccess}
                    message={workflowMessage}
                />

                {confirmConfig && (
                    <ConfirmModal
                        show={showConfirmModal}
                        onHide={() => {
                            setShowConfirmModal(false);
                            setConfirmConfig(null);
                        }}
                        onConfirm={() => {
                            setShowConfirmModal(false);
                            confirmConfig.onConfirm();
                            setConfirmConfig(null);
                        }}
                        title={confirmConfig.title}
                        message={confirmConfig.body}
                        confirmText={confirmConfig.confirmButtonText}
                        variant="warning"
                    />
                )}
                </div>
            </div>
        </>
    );
};
